import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import ToolTip from 'core/components/Tooltip';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import {
  BillingCodeDTO,
  MipsResponseDTO,
  VisitNoteBillingCodeDTO,
  VisitNoteMipsDTO,
} from 'dtos';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import {
  UseFieldArrayAppend,
  UseFieldArrayRemove,
} from 'react-hook-form';
import {
  useConfirmDialog,
  useNotify,
  useSelectableContext,
} from 'core/hooks';
import {
  VisitNoteData,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent';
import {
  patientEventChannel,
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  pluralize,
} from '@populateui/utils';
import {
  patientMedicalBackgroundSectionsById,
} from 'views/EMR/components/MedicalBackgroundDialog';
import {
  Patient,
} from 'pages/Dashboard/types/patient';
import Skeleton from 'core/components/Skeleton';
import {
  normalizeServiceCodeOption,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import compact from 'lodash/compact';
import {
  getRemovableReportingCodeIndexes,
  getRequiredReportingCodeIndexes,
  getRequiredReportingCodes,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/mipsReportingCodes';

type Props = {
  selectedMips: VisitNoteMipsDTO[];
  relatedMipsList: MipsResponseDTO[];
  patient: Patient;
  isLoading: boolean;
  billingCodes: VisitNoteBillingCodeDTO[];
  appendBillingCode: UseFieldArrayAppend<VisitNoteData, 'bill.serviceCodes'>;
  removeBillingCode: UseFieldArrayRemove;
};

type MessageProps = {
  code: string;
  variant: 'add' | 'remove';
};

function Message({ code, variant }: MessageProps) {
  return (
    <div>
      Billing Code
      {' '}
      <span className="font-semibold">{code}</span>
      {' '}
      was
      {' '}
      {variant === 'add' ? 'added' : 'removed'}
      .
    </div>
  );
}

type MessageAllProps = {
  codes: string[];
  variant: 'add' | 'remove';
};

function MessageAll({ codes, variant }: MessageAllProps) {
  return (
    <div>
      {pluralize('Billing Code', codes.length, false)}
      {' '}
      {codes.map((code, idx) => (
        <span key={code} className="font-semibold">
          {code}
          {idx !== codes.length - 1 ? ', ' : ' '}
        </span>
      ))}
      {pluralize('was', codes.length, false)}
      {' '}
      {variant === 'add' ? 'added' : 'removed'}
      .
    </div>
  );
}

export default function MIPS({
  selectedMips,
  relatedMipsList,
  patient,
  isLoading,
  billingCodes,
  appendBillingCode,
  removeBillingCode,
}: Props) {
  const hasMIPS = relatedMipsList.length > 0;

  const openSocialHistoryEditDialog = () => {
    patientEventChannel.emit('onEditMedicalBackground', {
      patient,
      id: patientMedicalBackgroundSectionsById?.socialHistory?.id,
    });
  };

  const {
    selected,
    toggle,
    clear,
    isSelected,
    add,
    remove,
  } = useSelectableContext();
  const { confirm, closeConfirm } = useConfirmDialog();
  const notify = useNotify();
  const priorSuggestedCodes = useRef<Map<number, string | undefined>>(new Map());
  const priorSuggestions = useRef<Map<number, MipsResponseDTO>>(new Map());
  const dismissedMissingSuggestions = useRef<Set<number>>(new Set());
  const [knownMipsSelections, setKnownMipsSelections] = useState<Map<number, VisitNoteMipsDTO>>(new Map());

  useEffect(() => {
    setKnownMipsSelections((existingSelections) => {
      const updatedSelections = new Map(existingSelections);
      let hasChanges = false;
      selectedMips.forEach((selection) => {
        const mipsId = selection.mipsId ?? selection.mips?.mipsId;
        if (!isNil(mipsId) && updatedSelections.get(mipsId) !== selection) {
          updatedSelections.set(mipsId, selection);
          hasChanges = true;
        }
      });
      relatedMipsList.forEach(({ mips }) => {
        const mipsId = mips?.mipsId;
        if (!isNil(mipsId) && updatedSelections.get(mipsId)?.mips !== mips) {
          updatedSelections.set(mipsId, {
            ...updatedSelections.get(mipsId),
            mipsId,
            mips,
          });
          hasChanges = true;
        }
      });
      return hasChanges ? updatedSelections : existingSelections;
    });
  }, [selectedMips, relatedMipsList]);

  useEffect(() => {
    add(...map(selectedMips, 'mips.mipsId'));
  }, [selectedMips, relatedMipsList]);

  const mipsList = useMemo(() => Array.from(selected)
    .map((mipsId) => knownMipsSelections.get(Number(mipsId)))
    .filter((selection): selection is VisitNoteMipsDTO => !isNil(selection)), [knownMipsSelections, selected]);

  useEffect(() => {
    visitNoteEventChannel.emit('onMIPSUpdate', mipsList);
  }, [mipsList]);

  const selectedBillingCodesByIndex = useMemo(() => (
    new Map(compact(billingCodes)?.map((item, index) => [item.code, index]))
  ), [billingCodes]);

  const getReportingCodeIndexes = (item: MipsResponseDTO) => {
    const requiredIndexes = getRequiredReportingCodeIndexes(item, billingCodes);
    if (requiredIndexes.length > 0) {
      return requiredIndexes;
    }

    const suggestedCodeIndex = selectedBillingCodesByIndex.get(item.billingCode?.code);
    return isNil(suggestedCodeIndex) ? [] : [suggestedCodeIndex];
  };

  const removeCodes = (item: MipsResponseDTO) => {
    const codeIndexes = getRemovableReportingCodeIndexes(
      {
        mipsId: item.mips?.mipsId,
        mips: item.mips,
      },
      mipsList,
      billingCodes,
    );
    const removedCodes = codeIndexes.map((index) => billingCodes[index]?.code ?? '');
    if (codeIndexes.length > 0) {
      removeBillingCode(codeIndexes);
      notify({
        message: <MessageAll codes={removedCodes} variant="remove" />,
      });
    }
  };

  const addCode = (billingCode: BillingCodeDTO) => {
    const toAdd = normalizeServiceCodeOption(billingCode);
    appendBillingCode(toAdd);
  };

  useEffect(() => {
    const currentMipsIds = new Set(compact(relatedMipsList.map((item) => item.mips?.mipsId)));
    const missingSelectedSuggestions = Array.from(priorSuggestions.current.entries())
      .filter(([mipsId]) => (
        isSelected(mipsId)
        && !currentMipsIds.has(mipsId)
        && !dismissedMissingSuggestions.current.has(mipsId)
      ));

    if (missingSelectedSuggestions.length > 0) {
      const missingMipsIds = missingSelectedSuggestions.map(([mipsId]) => mipsId);
      const missingItems = missingSelectedSuggestions.map(([, item]) => item);
      const remainingSelections = mipsList.filter((selection) => (
        !missingMipsIds.includes(selection.mipsId ?? selection.mips?.mipsId ?? -1)
      ));
      const codeIndexes = Array.from(new Set(missingItems.flatMap((item) => (
        getRemovableReportingCodeIndexes(
          {
            mipsId: item.mips?.mipsId,
            mips: item.mips,
          },
          remainingSelections,
          billingCodes,
        )
      ))));
      const selectedCodes = codeIndexes.map((index) => billingCodes[index]?.code ?? '');
      missingMipsIds.forEach((mipsId) => dismissedMissingSuggestions.current.add(mipsId));

      confirm({
        title: 'Remove MIPS result?',
        confirmText: 'Remove code and MIPS result',
        cancelText: 'Keep both',
        className: '!w-[32rem]',
        content: 'The screening response no longer produces this selected MIPS result. Removing it will also remove its reporting code. Are you sure you want to continue?',
        onConfirm: () => {
          if (codeIndexes.length > 0) {
            removeBillingCode(codeIndexes);
            notify({
              message: <MessageAll codes={selectedCodes} variant="remove" />,
            });
          }
          remove(...missingMipsIds);
          closeConfirm();
        },
      });
    }

    relatedMipsList.forEach((item) => {
      const mipsId = item.mips?.mipsId;
      if (isNil(mipsId)) {
        return;
      }

      priorSuggestions.current.set(mipsId, item);
      dismissedMissingSuggestions.current.delete(mipsId);
      const suggestedCode = item.billingCode?.code ?? undefined;
      const hadPreviousSuggestion = priorSuggestedCodes.current.has(mipsId);
      const previousSuggestedCode = priorSuggestedCodes.current.get(mipsId);
      priorSuggestedCodes.current.set(mipsId, suggestedCode);

      if (!hadPreviousSuggestion
        || previousSuggestedCode === suggestedCode
        || !isSelected(mipsId)
        || isNil(suggestedCode)) {
        return;
      }

      const requiredCodes = new Set(getRequiredReportingCodes(item));
      const conflictingCodeIndexes = billingCodes.reduce<number[]>((indexes, code, index) => {
        if (requiredCodes.has(code.code ?? '') && code.code !== suggestedCode) {
          indexes.push(index);
        }
        return indexes;
      }, []);
      if (conflictingCodeIndexes.length > 0) {
        removeBillingCode(conflictingCodeIndexes);
      }
      if (!billingCodes.some(({ code }) => code === suggestedCode)) {
        addCode(item.billingCode!);
        notify({
          message: <Message code={suggestedCode} variant="add" />,
        });
      }
    });
  }, [relatedMipsList]);

  const hasBillingCode = (item: MipsResponseDTO) => !isNil(item?.billingCode);
  const isMipsSelected = (item: MipsResponseDTO) => isSelected(item?.mips?.mipsId ?? '');
  const billingCodesSet = new Set(map(billingCodes, 'code'));
  const isBillingCodeUnique = (item: MipsResponseDTO) => (
    !billingCodesSet.has(item?.billingCode?.code)
  );

  const handleChangeMip = useCallback(
    (item: MipsResponseDTO) => {
      if (isMipsSelected(item)) {
        const removeSelection = () => {
          removeCodes(item);
          toggle(item?.mips?.mipsId ?? '');
          closeConfirm();
        };
        if (getReportingCodeIndexes(item).length === 0) {
          removeSelection();
          return;
        }

        confirm({
          title: 'Remove MIPS result?',
          confirmText: 'Remove code and MIPS result',
          cancelText: 'Keep both',
          className: '!w-[32rem]',
          content: (
            <div className="text-sm flex flex-col gap-2">
              <p>
                Removing MIPS Measure
                {' '}
                <span className="font-semibold">{item.mips?.measureId}</span>
                {' '}
                will also remove its reporting code.
              </p>
              <p>Are you sure you want to continue?</p>
            </div>
          ),
          onConfirm: removeSelection,
        });
        return;
      }

      const requiredCodes = new Set(getRequiredReportingCodes(item));
      const expectedCode = item.billingCode?.code;
      const conflictingCodeIndexes = billingCodes.reduce<number[]>((indexes, code, index) => {
        if (requiredCodes.has(code.code ?? '') && code.code !== expectedCode) {
          indexes.push(index);
        }
        return indexes;
      }, []);
      if (conflictingCodeIndexes.length > 0) {
        removeBillingCode(conflictingCodeIndexes);
      }
      if (hasBillingCode(item) && isBillingCodeUnique(item)) {
        addCode(item.billingCode!);
        notify({
          message: <Message code={item?.billingCode?.code!} variant="add" />,
        });
      }
      toggle(item?.mips?.mipsId ?? '');
    },
    [billingCodes, selected],
  );

  const handleSelectAll = useCallback(() => {
    const newlySelectedMips = relatedMipsList.filter((item) => !isMipsSelected(item));
    const conflictingCodeIndexes = Array.from(new Set(newlySelectedMips.flatMap((item) => {
      const requiredCodes = new Set(getRequiredReportingCodes(item));
      return billingCodes.reduce<number[]>((indexes, code, index) => {
        if (requiredCodes.has(code.code ?? '') && code.code !== item.billingCode?.code) {
          indexes.push(index);
        }
        return indexes;
      }, []);
    })));
    if (conflictingCodeIndexes.length > 0) {
      removeBillingCode(conflictingCodeIndexes);
    }

    const codesForMessage = [] as string[];
    newlySelectedMips.forEach((item) => {
      if (hasBillingCode(item) && isBillingCodeUnique(item)) {
        addCode(item.billingCode!);
        codesForMessage.push(item?.billingCode?.code!);
      }
    });
    add(...map(relatedMipsList, 'mips.mipsId'));
    if (codesForMessage.length > 0) {
      notify({
        message: <MessageAll codes={codesForMessage} variant="add" />,
      });
    }
  }, [selected, relatedMipsList, billingCodes]);

  const handleDeselectAll = useCallback(() => {
    const codeIndexes = Array.from(new Set(relatedMipsList.flatMap(getReportingCodeIndexes)));
    const selectedCodes = codeIndexes.map((index) => billingCodes[index]?.code ?? '');
    removeBillingCode(codeIndexes);
    if (selectedCodes.length > 0) {
      notify({
        message: <MessageAll codes={selectedCodes} variant="remove" />,
      });
    }
    clear();
    closeConfirm();
  }, [selectedBillingCodesByIndex, relatedMipsList, billingCodes]);

  const isAllSelected = useMemo(() => relatedMipsList?.every((item) => (
    isMipsSelected(item))), [relatedMipsList, isMipsSelected]);

  const handleToggleAll = useCallback(() => {
    if (isAllSelected) {
      const hasReportingCodes = relatedMipsList.some((item) => (
        getReportingCodeIndexes(item).length > 0
      ));
      if (hasReportingCodes) {
        confirm({
          title: 'Remove all MIPS results?',
          confirmText: 'Remove codes and MIPS results',
          cancelText: 'Keep all',
          content: 'Removing the selected MIPS results will also remove their reporting codes. Are you sure you want to continue?',
          onConfirm: handleDeselectAll,
        });
      } else {
        handleDeselectAll();
      }
    } else {
      handleSelectAll();
    }
  }, [relatedMipsList, handleDeselectAll, handleSelectAll]);

  useEffect(() => {
    const unsubscribeFromMipsClearChannel = visitNoteEventChannel.on('onMIPSUpdate', (mipsList) => {
      const nextSelected = new Set(compact(mipsList.map((item) => item.mipsId ?? item.mips?.mipsId)));
      const selectionsToRemove = Array.from(selected).filter((mipsId) => !nextSelected.has(mipsId as number));
      const selectionsToAdd = Array.from(nextSelected).filter((mipsId) => !selected.has(mipsId));
      if (selectionsToRemove.length > 0) {
        remove(...selectionsToRemove);
      }
      if (selectionsToAdd.length > 0) {
        add(...selectionsToAdd);
      }
    });

    return () => {
      unsubscribeFromMipsClearChannel();
    };
  }, [selected]);

  return (
    <div className={clsx('gap-2 flex flex-col p-4 -mx-4 text-xs', {
      'bg-blue-100': hasMIPS && !isLoading,
      'bg-amber-100': !hasMIPS && !isLoading,
    })}
    >
      {hasMIPS && (
        <>
          <Checkbox
            id="toggle-all-mips"
            indeterminate={selected.size > 0 && !isAllSelected}
            onChange={handleToggleAll}
            checked={isAllSelected}
            label="MIPS Tips"
            color="secondary"
            classes={{
              root: clsx('!w-fit', { '!border-b !border-b-blue-400 pb-2 pr-8': hasMIPS }),
              label: '!font-semibold',
              checkbox: clsx({ '!hidden': !hasMIPS }),
            }}
          />
          <div className="flex flex-col gap-2">
            {relatedMipsList.map((item: MipsResponseDTO) => (
              <ToolTip
                key={item.mips?.mipsId}
                title={(
                  <div className="flex flex-col gap-3">
                    <div className="flex gap-1">
                      <span>Code:</span>
                      <span className="font-semibold">{item.billingCode?.code ?? 'No code available'}</span>
                    </div>
                    <div>{item.mips?.description ?? 'No description available'}</div>
                  </div>
                )}
              >
                <div>
                  <Checkbox
                    classes={{ label: 'flex flex-row justify-between w-full !ml-0' }}
                    containerClassName="w-full"
                    color="secondary"
                    label={{ text: item.mips?.tip, placement: 'start' }}
                    checked={isSelected(item?.mips?.mipsId ?? '')}
                    onChange={() => { handleChangeMip(item as MipsResponseDTO); }}
                    id={item.mips?.tip ?? ''}
                  />
                </div>
              </ToolTip>
            ))}
          </div>
        </>
      )}
      {isLoading && (
        <div className="flex-1 gap-2 flex flex-col">
          <Skeleton className="!w-52 !h-4" />
          <Skeleton className="!w-72 !h-4" />
        </div>
      )}
      {!isLoading && relatedMipsList.length === 0 && (
        <>
          <p className="text-amber-800 flex items-center gap-1 font-semibold">
            <ErrorOutlineIcon className="!w-4 !h-4" />
            Not Enough Information for MIPS TIPS
          </p>
          <p className="text-amber-800">
            <span>Chart does not have enough information to populate MIPS tips.</span>
            {' '}
            <button
              className="font-medium underline cursor-pointer"
              onClick={openSocialHistoryEditDialog}
              type="button"
            >
              Consider asking the MIPS questions.
            </button>
          </p>
        </>
      )}
    </div>
  );
}
